import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchWithAuth } from '../services/api';
import {
  isDevelopmentEnvironment,
  isTestSiteKey,
  handleCaptchaToken,
  getCaptchaErrorMessage,
  generateDummyToken,
} from '../utils/captcha-utils';

const REASON_KEYS = ['spam', 'offensive', 'false', 'other'];

const ReportCommentModal = ({ isOpen, onClose, onSubmit, isSubmitting, isSuccess, subject = 'comment' }) => {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState('');
  const [otherText, setOtherText] = useState('');

  // CAPTCHA state
  const [captchaToken, setCaptchaToken] = useState('');
  const [isCaptchaLoaded, setIsCaptchaLoaded] = useState(false);
  const [captchaWidgetId, setCaptchaWidgetId] = useState(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [captchaSiteKey, setCaptchaSiteKey] = useState('1x00000000000000000000AA');
  const [captchaError, setCaptchaError] = useState('');
  const captchaContainerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedReason('');
      setOtherText('');
      setCaptchaToken('');
      setCaptchaError('');
      setCaptchaKey(prev => prev + 1);
    }
  }, [isOpen]);

  // Fetch captcha site key
  useEffect(() => {
    if (!isOpen) return;
    const fetchCaptchaConfig = async () => {
      try {
        const config = await fetchWithAuth('/auth/captcha-config', { isPublic: true });
        setCaptchaSiteKey(config.siteKey);
      } catch {
        // keep fallback test key
      }
    };
    fetchCaptchaConfig();
  }, [isOpen]);

  // Load turnstile script
  useEffect(() => {
    if (!isOpen) return;
    let active = true;

    if (window.turnstile) {
      if (active) setIsCaptchaLoaded(true);
      return;
    }

    const existing = document.querySelector('script[src*="turnstile"]');
    if (existing) {
      const onLoad = () => { if (active) setIsCaptchaLoaded(true); };
      existing.addEventListener('load', onLoad);
      return () => { active = false; existing.removeEventListener('load', onLoad); };
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.id = 'turnstile-script-report';
    const onLoad = () => { if (active) setIsCaptchaLoaded(true); };
    script.addEventListener('load', onLoad);
    document.head.appendChild(script);

    return () => {
      active = false;
      script.removeEventListener('load', onLoad);
      const s = document.getElementById('turnstile-script-report');
      if (s) s.remove();
    };
  }, [isOpen]);

  // Render CAPTCHA widget
  useEffect(() => {
    if (!isOpen || !isCaptchaLoaded || !window.turnstile || !captchaContainerRef.current || !captchaSiteKey) return;

    if (captchaWidgetId !== null) {
      try { window.turnstile.remove(captchaWidgetId); } catch {}
      setCaptchaWidgetId(null);
    }

    const timer = setTimeout(() => {
      try {
        const id = window.turnstile.render(captchaContainerRef.current, {
          sitekey: captchaSiteKey,
          callback: (token) => {
            const processed = handleCaptchaToken(token, captchaSiteKey);
            setCaptchaToken(processed);
            setCaptchaError('');
          },
          'error-callback': (code) => {
            if (isDevelopmentEnvironment() && isTestSiteKey(captchaSiteKey)) {
              setCaptchaToken(generateDummyToken());
              setCaptchaError('');
              return;
            }
            setCaptchaToken('');
            setCaptchaError(getCaptchaErrorMessage(code, captchaSiteKey));
          },
          'expired-callback': () => {
            setCaptchaToken('');
            setCaptchaError(t('profile.commentValidationCaptchaRequired', 'CAPTCHA expired. Please solve it again.'));
          },
          theme: 'light',
          size: 'normal',
          retry: 'auto',
          'refresh-expired': 'auto',
        });
        setCaptchaWidgetId(id);
      } catch {}
    }, 300);

    return () => { clearTimeout(timer); };
  }, [isOpen, isCaptchaLoaded, captchaKey, captchaSiteKey]);

  // Cleanup widget on unmount
  useEffect(() => {
    return () => {
      if (captchaWidgetId !== null && window.turnstile) {
        try { window.turnstile.remove(captchaWidgetId); } catch {}
      }
    };
  }, []);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedReason) return;
    if (!captchaToken) {
      setCaptchaError(t('profile.commentValidationCaptchaRequired', 'Please complete the CAPTCHA verification.'));
      return;
    }
    const label = t(`profile.reportReason${selectedReason.charAt(0).toUpperCase() + selectedReason.slice(1)}`, selectedReason);
    const finalReason = selectedReason === 'other' && otherText.trim()
      ? `${label}: ${otherText.trim()}`
      : label;
    onSubmit(finalReason, captchaToken);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={!isSubmitting ? onClose : undefined}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        {isSuccess ? (
          <div className="flex flex-col items-center text-center py-4 gap-4">
            <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center">
              <svg className="w-7 h-7 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-slate-800 font-medium text-base">
              {subject === 'profile'
                ? t('profile.reportProfileSuccess', 'Profile reported. Thank you!')
                : t('profile.reportCommentSuccess', 'Comment reported. Thank you!')}
            </p>
            <button
              onClick={onClose}
              className="mt-1 px-5 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
            >
              {t('profile.reportCommentCancel', 'Close')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <h2 id="report-modal-title" className="text-lg font-semibold text-slate-900">
                {subject === 'profile'
                  ? t('profile.reportProfileModalTitle', 'Report Profile')
                  : t('profile.reportCommentModalTitle', 'Report Comment')}
              </h2>
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                aria-label="Close"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Reason label */}
            <p className="text-sm font-medium text-slate-700 mb-3">
              {t('profile.reportCommentReason', 'Select a reason')}
            </p>

            {/* Reason options */}
            <div className="space-y-2 mb-6">
              {REASON_KEYS.map((key) => {
                const labelKey = `profile.reportReason${key.charAt(0).toUpperCase() + key.slice(1)}`;
                const defaultLabels = {
                  spam: 'Spam',
                  offensive: 'Offensive / Inappropriate',
                  false: 'False information',
                  other: 'Other',
                };
                return (
                  <label
                    key={key}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all duration-150 ${
                      selectedReason === key
                        ? 'border-red-400 bg-red-50 text-red-700'
                        : 'border-slate-200 hover:border-slate-300 text-slate-700'
                    }`}
                  >
                    <input
                      type="radio"
                      name="reportReason"
                      value={key}
                      checked={selectedReason === key}
                      onChange={() => { setSelectedReason(key); setOtherText(''); }}
                      className="accent-red-500"
                    />
                    <span className="text-sm font-medium">
                      {t(labelKey, defaultLabels[key])}
                    </span>
                  </label>
                );
              })}
            </div>

            {/* Free-text field for "Other" */}
            {selectedReason === 'other' && (
              <div className="mb-5 -mt-3">
                <textarea
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  maxLength={300}
                  rows={3}
                  placeholder={t('profile.reportOtherPlaceholder', 'Please describe the issue...')}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl resize-none focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200 text-slate-700 placeholder-slate-400"
                />
              </div>
            )}

            {/* CAPTCHA */}
            <div className="mb-5">
              <div ref={captchaContainerRef} key={captchaKey} className="flex justify-center" />
              {captchaError && (
                <p className="mt-1.5 text-xs text-red-600">{captchaError}</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                {t('profile.reportCommentCancel', 'Cancel')}
              </button>
              <button
                type="submit"
                disabled={!selectedReason || !captchaToken || isSubmitting}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    <span>...</span>
                  </>
                ) : (
                  t('profile.reportCommentSubmit', 'Submit Report')
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReportCommentModal;
